import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile } from '../api/auth';
import { getBranding } from '../api/branding';
import { getSubscription } from '../api/subscription';
import { getMyPermissions } from '../api/rbac';
import { useAuthStore } from '../stores/authStore';
import { useBrandingStore } from '../stores/brandingStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { ROUTES } from '../constants/routes';
import type { ApiError } from '../types/api.types';

export function useBootstrap() {
  const [isReady, setIsReady] = useState(false);
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setIsReady(true);
      return;
    }

    // Reset bootstrap gate so ModuleGuard blocks while fresh data loads for this session
    useSubscriptionStore.getState().resetBootstrap();
    setIsReady(false);

    Promise.all([getProfile(), getBranding()])
      .then(async ([profile, branding]) => {
        useAuthStore.getState().setAuth(profile.user, token, profile.company);
        useBrandingStore.getState().setBranding(branding);

        // Broker users don't have subscriptions — skip the subscription fetch
        const isBroker = useAuthStore.getState().isIcBroker();
        if (!isBroker) {
          getSubscription()
            .then((sub) => { if (sub) useSubscriptionStore.getState().setSubscription(sub); })
            .catch(() => {/* silent */});
        }

        // Load effective permissions in the background — non-blocking
        getMyPermissions()
          .then((perms) => useAuthStore.getState().setEffectivePermissions(perms.effective as string[] | '*'))
          .catch(() => {/* silently ignored */});

        if (profile.user?.must_change_password) {
          navigate(ROUTES.FIRST_LOGIN_CHANGE_PASSWORD, { replace: true });
        } else if (isBroker) {
          navigate(ROUTES.BROKER_DASHBOARD, { replace: true });
        }
      })
      .catch((err: ApiError) => {
        if (err.statusCode === 401) {
          useAuthStore.getState().clearAuth();
        }
        // Network errors: silent fail, stay logged in
      })
      .finally(() => {
        useSubscriptionStore.getState().setBootstrapComplete();
        setIsReady(true);
      });
  // Re-run whenever the token changes (login/logout in the same session)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return { isReady };
}
